import React, { useState, useEffect } from 'react';
import { doc, updateDoc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import { useUserRole } from '../../../hooks/useUserRole';
import { AlertTriangle } from 'lucide-react';

export const UserPermissionsSettings = () => {
  const { isManager } = useUserRole();
  const [autoApproveUsers, setAutoApproveUsers] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settingsRef = doc(db, 'settings', 'userPermissions');
        const settingsDoc = await getDoc(settingsRef);
        
        if (!settingsDoc.exists()) {
          // Initialize settings if they don't exist
          await setDoc(settingsRef, {
            autoApproveUsers: false,
            excludedRoles: ['manager'],
            updatedAt: new Date()
          });
          setAutoApproveUsers(false);
        } else {
          setAutoApproveUsers(settingsDoc.data().autoApproveUsers || false);
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
        setError('Einstellungen konnten nicht geladen werden');
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const handleAutoApproveChange = async (enabled: boolean) => {
    if (!isManager) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const settingsRef = doc(db, 'settings', 'userPermissions');
      await setDoc(settingsRef, {
        autoApproveUsers: enabled,
        excludedRoles: ['manager'],
        updatedAt: new Date()
      }, { merge: true });

      setAutoApproveUsers(enabled);
      setSuccess('Einstellungen wurden erfolgreich gespeichert');
    } catch (error) {
      console.error('Error updating user permissions:', error);
      setError('Einstellungen konnten nicht gespeichert werden. Bitte stellen Sie sicher, dass Sie die erforderlichen Berechtigungen haben.');
    } finally {
      setLoading(false);
    }
  };

  if (!isManager) {
    return (
      <div className="rounded-md bg-yellow-50 dark:bg-yellow-900/50 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <AlertTriangle className="h-5 w-5 text-yellow-400" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
              Zugriff verweigert
            </h3>
            <p className="mt-2 text-sm text-yellow-700 dark:text-yellow-200">
              Sie haben keine Berechtigung, diese Einstellungen zu ändern.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
          Benutzerrechte
        </h3>
        <div className="mt-6">
          <div className="flex items-center justify-between">
            <div className="flex-grow flex flex-col">
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                Automatische Benutzerfreischaltung
              </span>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Reiter und Mitreiter werden nach der Registrierung automatisch freigeschaltet. 
                Verwalter müssen weiterhin manuell freigeschaltet werden.
              </span>
            </div>
            <button
              onClick={() => handleAutoApproveChange(!autoApproveUsers)}
              disabled={loading}
              className={`
                relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent 
                transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2
                ${autoApproveUsers ? 'bg-indigo-600' : 'bg-gray-200'}
                ${loading ? 'opacity-50 cursor-not-allowed' : ''}
              `}
            >
              <span
                className={`
                  pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 
                  transition duration-200 ease-in-out
                  ${autoApproveUsers ? 'translate-x-5' : 'translate-x-0'}
                `}
              />
            </button>
          </div>

          {error && (
            <div className="mt-4 rounded-md bg-red-50 dark:bg-red-900/50 p-4">
              <p className="text-sm text-red-700 dark:text-red-200">{error}</p>
            </div>
          )}

          {success && (
            <div className="mt-4 rounded-md bg-green-50 dark:bg-green-900/50 p-4">
              <p className="text-sm text-green-700 dark:text-green-200">{success}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};